import { useCallback } from "react";
import { App } from "antd";
import { useTranslation } from "react-i18next";
import type { SchemaElement, SchemaRegion } from "../../../api/restaurantSchema";
import {
  useCreateTable,
  useCreateTablesRegion,
  useDeleteTable,
  useUpdateTable,
} from "../../../hooks/useRestaurantSchema";
import { getApiErrorMessage } from "../../../utils/translations";
import { draftTable, placeTableInRegion } from "../../../components/restaurant-schema/autoLayout";

/** Най-много толкова маси наведнъж в „Серия номера". */
export const MAX_SERIES_TABLES = 100;

const isConflict = (error: unknown) =>
  (error as { response?: { status?: number } } | null)?.response?.status === 409;

/**
 * Записите на „Конфигурация на маси": заявката и съобщението. Позицията на
 * новата/преместената маса се смята тук (autoLayout) — BE само я прибира в
 * региона. Регион, в който масата не се събира, пак я приема, но казваме да се
 * подреди в схемата. Груповите действия (серия, места/местене/изтриване на
 * избраните) пращат заявките ЕДНА ПО ЕДНА — всяка следваща маса се нарежда
 * спрямо вече добавените/преместените, а отказ на една не спира останалите.
 */
export const useTablesConfigActions = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const createTableMutation = useCreateTable();
  const updateTableMutation = useUpdateTable();
  const deleteTableMutation = useDeleteTable();
  const createRegionMutation = useCreateTablesRegion();

  // Отказите на BE са изречения за четене („Маса 7 има резервация…") —
  // затова по-дълго от стандартните 3 s.
  const reportError = useCallback(
    (error: unknown, fallbackKey = "tablesConfig.messages.saveError") =>
      message.error({ content: getApiErrorMessage(error, t(fallbackKey)), duration: 6 }),
    [message, t],
  );

  const warnNotFitted = useCallback(
    () => message.warning({ content: t("tablesConfig.messages.notFitted"), duration: 6 }),
    [message, t],
  );

  const createRegion = async (name: string) => {
    try {
      const created = await createRegionMutation.mutateAsync({ name });
      message.success(t("restaurantSchema.messages.regionCreated"));
      return created;
    } catch (error) {
      reportError(error);
      throw error;
    }
  };

  // Една маса на първото свободно място сред `elements` (без съобщения).
  const sendCreate = async (
    region: SchemaRegion,
    elements: SchemaElement[],
    name: string,
    seats: number,
  ) => {
    const placement = placeTableInRegion({ shape: region.shape, elements }, draftTable(region.id, seats));
    const created = await createTableMutation.mutateAsync({
      name,
      seats,
      regionId: region.id,
      x: placement.x,
      y: placement.y,
    });
    return { created, fitted: placement.fitted };
  };

  /** Нова маса в `region` на първото свободно място. Хвърля при отказ (след съобщението). */
  const createTable = async (region: SchemaRegion, name: string, seats: number) => {
    try {
      const { created, fitted } = await sendCreate(region, region.elements, name, seats);
      if (fitted) {
        message.success(t("tablesConfig.messages.created", { name: created.name ?? name }));
      } else {
        warnNotFitted();
      }
      return created;
    } catch (error) {
      reportError(error);
      throw error;
    }
  };

  /**
   * Серия маси с готовите номера (заетите вече са махнати от формата). Номер,
   * зает междувременно от друг (409), се пропуска; друга грешка спира серията.
   */
  const createTableSeries = async (region: SchemaRegion, names: string[], seats: number) => {
    const elements = [...region.elements];
    const skipped: string[] = [];
    let createdCount = 0;
    let allFitted = true;
    for (const name of names.slice(0, MAX_SERIES_TABLES)) {
      try {
        const { created, fitted } = await sendCreate(region, elements, name, seats);
        elements.push(created);
        createdCount += 1;
        allFitted &&= fitted;
      } catch (error) {
        if (isConflict(error)) {
          skipped.push(name);
          continue;
        }
        reportError(error);
        break;
      }
    }
    if (createdCount > 0) {
      message.success(t("tablesConfig.messages.seriesCreated", { count: createdCount }));
    }
    if (skipped.length > 0) {
      message.warning({
        content: t("tablesConfig.messages.seriesSkipped", { names: skipped.join(", ") }),
        duration: 6,
      });
    }
    if (!allFitted) warnNotFitted();
    return createdCount;
  };

  // Оптимистично: провалът връща кеша (и полето) към записаното.
  const renameTable = (table: SchemaElement, name: string) =>
    updateTableMutation
      .mutateAsync({ id: table.id, data: { name } })
      .catch((error) => reportError(error));

  // Местата не местят масата — ако порасне в съседка, подрежда се в схемата.
  const changeSeats = (table: SchemaElement, seats: number) =>
    updateTableMutation
      .mutateAsync({ id: table.id, data: { seats } })
      .catch((error) => reportError(error));

  /** Едни и същи места на всички избрани (без заявка за тези, които ги имат). */
  const setSeatsForTables = async (tables: SchemaElement[], seats: number) => {
    let failed = false;
    let changedCount = 0;
    for (const table of tables) {
      if (table.seats === seats) continue;
      try {
        await updateTableMutation.mutateAsync({ id: table.id, data: { seats } });
        changedCount += 1;
      } catch (error) {
        if (!failed) reportError(error);
        failed = true;
      }
    }
    if (!failed && changedCount > 0) {
      message.success(t("tablesConfig.messages.seatsApplied", { count: changedCount }));
    }
  };

  /** Премества масите в друг регион — всяка на първото свободно място там. */
  const moveTables = async (tables: SchemaElement[], target: SchemaRegion) => {
    const elements = [...target.elements];
    let movedCount = 0;
    let allFitted = true;
    let failed = false;
    for (const table of tables) {
      if (table.regionId === target.id) continue;
      const moving = { ...table, regionId: target.id };
      const placement = placeTableInRegion({ shape: target.shape, elements }, moving);
      try {
        await updateTableMutation.mutateAsync({
          id: table.id,
          data: { regionId: target.id, x: placement.x, y: placement.y },
        });
        elements.push({ ...moving, x: placement.x, y: placement.y });
        movedCount += 1;
        allFitted &&= placement.fitted;
      } catch (error) {
        if (!failed) reportError(error);
        failed = true;
      }
    }
    if (movedCount === 0) return;
    if (!allFitted) {
      warnNotFitted();
    } else if (movedCount === 1 && tables.length === 1) {
      message.success(t("tablesConfig.messages.moved", { name: tables[0].name ?? "", region: target.name }));
    } else {
      message.success(t("tablesConfig.messages.movedMany", { count: movedCount, region: target.name }));
    }
  };

  const moveTable = (table: SchemaElement, target: SchemaRegion) => moveTables([table], target);

  /** Изтрива; връща false при отказ (409 — резервация/отворена сметка). */
  const deleteTable = async (table: SchemaElement) => {
    try {
      await deleteTableMutation.mutateAsync(table.id);
      message.success(t("tablesConfig.messages.deleted", { name: table.name ?? "" }));
      return true;
    } catch (error) {
      reportError(error, "tablesConfig.messages.deleteError");
      return false;
    }
  };

  /**
   * Изтрива избраните една по една; масите в употреба (409) остават и се
   * изброяват. Връща id-тата на изтритите.
   */
  const deleteTables = async (tables: SchemaElement[]) => {
    if (tables.length === 1) return (await deleteTable(tables[0])) ? [tables[0].id] : [];
    const deletedIds: number[] = [];
    const inUse: string[] = [];
    let otherError: unknown = null;
    for (const table of tables) {
      try {
        await deleteTableMutation.mutateAsync(table.id);
        deletedIds.push(table.id);
      } catch (error) {
        if (isConflict(error)) inUse.push(table.name ?? "");
        else otherError ??= error;
      }
    }
    if (deletedIds.length > 0) {
      message.success(t("tablesConfig.messages.deletedMany", { count: deletedIds.length }));
    }
    if (inUse.length > 0) {
      message.warning({
        content: t("tablesConfig.messages.deleteSkippedInUse", { names: inUse.join(", ") }),
        duration: 8,
      });
    }
    if (otherError) reportError(otherError, "tablesConfig.messages.deleteError");
    return deletedIds;
  };

  return {
    createRegion,
    createTable,
    createTableSeries,
    renameTable,
    changeSeats,
    setSeatsForTables,
    moveTable,
    moveTables,
    deleteTable,
    deleteTables,
  };
};

export type TablesConfigActions = ReturnType<typeof useTablesConfigActions>;
